import React, { useState, useEffect, useRef } from 'react';
import { X, FileDown, Check, ChevronUp, ChevronDown } from 'lucide-react';
import type { PdfExportOptions } from '../utils/pdfExport';
import { DEFAULT_PDF_OPTIONS } from '../utils/pdfExport';

interface Props {
  open: boolean;
  onClose: () => void;
  onExport: (options: PdfExportOptions) => void;
}

const STORAGE_KEY = 'apex-pdf-options';
const ORDER_KEY  = 'apex-pdf-section-order';

type SectionKey = keyof Pick<
  PdfExportOptions,
  'includeOverview' | 'includePlan' | 'includeWarmup' | 'includeVolumeSummary' | 'includeTrainingJournal'
>;

const ALL_SECTION_DEFS: { key: SectionKey; label: string; description: string }[] = [
  { key: 'includeOverview',        label: 'Pogląd programu',           description: 'Tabela z parametrami: cel, poziom, częstotliwość, progresja' },
  { key: 'includePlan',            label: 'Plan treningowy',            description: 'Pełen rozkład tygodni, sesji i ćwiczeń' },
  { key: 'includeWarmup',          label: 'Rozgrzewka',                 description: 'Sekcja rozgrzewkowa w każdej sesji (wymaga „Plan treningowy“)' },
  { key: 'includeVolumeSummary',   label: 'Podsumowanie objętości',    description: 'Tabela objętości na mięsień (serie bezpośrednie/pośrednie, MEV/MAV/MRV)' },
  { key: 'includeTrainingJournal', label: 'Dziennik treningowy',        description: 'Tabele do druku - pola na ciężar, powt., RPE, RIR, tempo + podsumowanie sesji' },
];

const DEFAULT_ORDER: SectionKey[] = ALL_SECTION_DEFS.map((s) => s.key);

function loadSaved(): Partial<PdfExportOptions> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function loadSavedOrder(): SectionKey[] {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return DEFAULT_ORDER;
    const parsed: SectionKey[] = JSON.parse(raw);
    // ensure all keys are present (in case we add new sections later)
    const merged = [...parsed.filter((k) => DEFAULT_ORDER.includes(k))];
    for (const k of DEFAULT_ORDER) if (!merged.includes(k)) merged.push(k);
    return merged;
  } catch {
    return DEFAULT_ORDER;
  }
}

function savePersist(opts: PdfExportOptions, order: SectionKey[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(opts));
  localStorage.setItem(ORDER_KEY,  JSON.stringify(order));
}

export default function PdfExportModal({ open, onClose, onExport }: Props) {
  const [opts, setOpts] = useState<PdfExportOptions>(() => ({
    ...DEFAULT_PDF_OPTIONS,
    ...loadSaved(),
  }));
  const [sectionOrder, setSectionOrder] = useState<SectionKey[]>(loadSavedOrder);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setOpts((prev) => ({ ...prev, ...loadSaved() }));
      setSectionOrder(loadSavedOrder());
    }
  }, [open]);

  const moveSection = (idx: number, dir: -1 | 1) => {
    setSectionOrder((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  if (!open) return null;

  const toggle = (key: keyof PdfExportOptions) => {
    setOpts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = () => {
    const finalOpts: PdfExportOptions = { ...opts, sectionOrder };
    savePersist(finalOpts, sectionOrder);
    onExport(finalOpts);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  const orderedSections = sectionOrder.map((k) => ALL_SECTION_DEFS.find((s) => s.key === k)!);

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-down">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <FileDown size={20} className="text-brand-500" />
            <h2 className="text-lg font-bold">Eksport PDF</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Section toggles with reorder */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Zawartość PDF
              </h3>
              <span className="text-[10px] text-slate-400 dark:text-slate-500">
                Kolejność sekcji w dokumencie
              </span>
            </div>
            <div className="space-y-1.5">
              {orderedSections.map((s, idx) => {
                const checked = !!opts[s.key];
                const disabled = s.key === 'includeWarmup' && !opts.includePlan;
                return (
                  <div
                    key={s.key}
                    className={`flex items-center gap-2 p-3 rounded-xl border transition ${
                      disabled
                        ? 'opacity-40 border-slate-100 dark:border-slate-800'
                        : checked
                        ? 'border-brand-300 dark:border-brand-700 bg-brand-50/50 dark:bg-brand-950/30'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    {/* Order buttons */}
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => moveSection(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 rounded text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 disabled:opacity-20 disabled:cursor-not-allowed transition"
                        title="Prześ wyżej"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSection(idx, 1)}
                        disabled={idx === orderedSections.length - 1}
                        className="p-0.5 rounded text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 disabled:opacity-20 disabled:cursor-not-allowed transition"
                        title="Prześ niżej"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </div>

                    {/* Position badge */}
                    <span className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-[9px] font-bold text-slate-400">
                      {idx + 1}
                    </span>

                    {/* Checkbox */}
                    <div
                      className={`shrink-0 w-5 h-5 rounded flex items-center justify-center border-2 transition cursor-pointer ${
                        checked
                          ? 'bg-brand-500 border-brand-500'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                      onClick={() => !disabled && toggle(s.key)}
                    >
                      {checked && <Check size={12} className="text-white" />}
                    </div>

                    {/* Label */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => !disabled && toggle(s.key)}
                    >
                      <span className="text-sm font-medium">{s.label}</span>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 leading-tight">
                        {s.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Watermark */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Znaki wodne
            </h3>

            {/* Diagonal watermark */}
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Znak wodny (po przekątnej) - zostaw puste aby wyłączyć
              </label>
              <input
                type="text"
                value={opts.watermarkText}
                onChange={(e) => setOpts((prev) => ({ ...prev, watermarkText: e.target.value }))}
                placeholder="np. Jan Kowalski"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition placeholder:text-slate-300 dark:placeholder:text-slate-600"
              />
            </div>

            {/* Footer badge */}
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Podpis w stopce (dół strony) - zostaw puste aby wyłączyć
              </label>
              <input
                type="text"
                value={opts.footerBadgeText}
                onChange={(e) => setOpts((prev) => ({ ...prev, footerBadgeText: e.target.value }))}
                placeholder="np. Certyfikowany trener: Jan Kowalski"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition placeholder:text-slate-300 dark:placeholder:text-slate-600"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <p className="text-[10px] text-slate-400">
            Ustawienia są zapamiętywane na tym urządzeniu
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              Anuluj
            </button>
            <button
              onClick={handleExport}
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm transition flex items-center gap-1.5"
            >
              <FileDown size={16} />
              Generuj PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
